<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { getInboxLabel } from 'dashboard/helper/inboxLabel';
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline */
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import TalkviaLeadCapturesAPI from 'dashboard/api/talkviaLeadCaptures';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useCorbanModule } from 'dashboard/composables/useCorbanModule';
import CustomDomainCard from './CustomDomainCard.vue';

const { isAdmin } = useAdmin();
const customDomain = ref(null);
const hasActiveCustomDomain = computed(
  () => customDomain.value?.status === 'active'
);
const loading = ref(true);
const saving = ref(false);
const captures = ref([]);
const websiteInboxes = ref([]);
const whatsappInboxes = ref([]);
const whatsappInboxOptions = computed(() =>
  whatsappInboxes.value.map(inbox => ({
    value: inbox.id,
    label: getInboxLabel(inbox),
  }))
);
const defaultLegalContent = ref({});
const selectedId = ref(null);
const activeTab = ref('config');

const defaultFields = () => [
  { key: 'fullName', enabled: true, required: true },
  { key: 'phone', enabled: true, required: true },
  { key: 'cpf', enabled: true, required: true },
  { key: 'email', enabled: false, required: false },
  { key: 'birthDate', enabled: false, required: false },
];

const defaultCapture = () => ({
  public_path: '',
  id: null,
  name: 'Nova captação',
  status: 'draft',
  product: 'leads',
  experience: 'form',
  template_key: 'split',
  editor_mode: 'template',
  inbox_id: websiteInboxes.value[0]?.id || '',
  form_fields: defaultFields(),
  design: {
    primary_color: '#2563EB',
    secondary_color: '#0F172A',
    background_color: '#F1F5F9',
    card_color: '#FFFFFF',
    text_color: '#0F172A',
  },
  content: {
    eyebrow: 'Simulação segura e sem compromisso',
    headline: 'Encontre uma opção de crédito que faça sentido para você',
    description:
      'Informe seus dados para receber uma análise inicial personalizada e continuar com uma equipe preparada para orientar você.',
    form_title: 'Comece sua análise gratuita',
    button_label: 'QUERO RECEBER MINHA ANÁLISE',
    success_message:
      'Cadastro concluído. Nossa equipe recebeu seus dados e dará continuidade ao atendimento por esta conversa.',
    post_submit_action: 'message',
    post_submit_button_label: 'Continuar atendimento',
    whatsapp_button_label: 'Continuar pelo WhatsApp',
    webchat_button_label: 'Conversar no site',
    whatsapp_number: '',
    whatsapp_inbox_ids: [],
    whatsapp_message:
      'Olá! Acabei de preencher a simulação e quero continuar meu atendimento.',
    webchat_inbox_id: '',
    experience_title: 'Descubra seu benefício',
    experience_description: 'Interaja para liberar sua análise personalizada.',
    experience_result: 'Análise prioritária liberada',
    experience_button_label: 'Continuar',
    roulette_options:
      'Análise grátis\nAtendimento rápido\nCondição especial\nSimulação personalizada',
    inactive_title: 'Esta captação está temporariamente indisponível',
    inactive_message:
      'A campanha foi encerrada ou pausada. Entre em contato com nossa equipe para saber mais.',
    use_default_terms: false,
    terms_of_use: '',
    privacy_policy: '',
  },
  seo: {
    title: '',
    description: '',
    keywords: '',
    canonical_url: '',
    noindex: false,
  },
  tracking: {
    ga4_id: '',
    google_ads_id: '',
    google_ads_label: '',
    meta_pixel_id: '',
    gtm_id: '',
  },
  custom_html:
    '<section class="hero">\n  <h1>Sua oportunidade começa aqui</h1>\n  <p>Preencha os dados para falar com nossa equipe.</p>\n</section>\n<div data-talkvia-form></div>',
  custom_js:
    "document.addEventListener('talkvia:submitted', event => {\n  console.log('Lead enviado', event.detail);\n});",
});

const editor = reactive(defaultCapture());

const legalText = key =>
  computed({
    get: () =>
      editor.content.use_default_terms
        ? defaultLegalContent.value[key] || ''
        : editor.content[key],
    set: value => {
      editor.content[key] = value;
    },
  });
const termsOfUse = legalText('terms_of_use');
const privacyPolicy = legalText('privacy_policy');

const fieldLabels = {
  fullName: 'Nome completo',
  phone: 'Telefone',
  cpf: 'CPF',
  email: 'E-mail',
  birthDate: 'Data de nascimento',
};

const tabs = [
  { key: 'config', label: 'Config', icon: 'i-lucide-sliders-horizontal' },
  { key: 'design', label: 'Design', icon: 'i-lucide-palette' },
  { key: 'content', label: 'Conteúdo', icon: 'i-lucide-layout-list' },
  {
    key: 'seo',
    label: 'SEO e pixels',
    icon: 'i-lucide-chart-no-axes-combined',
  },
  { key: 'code', label: 'HTML / JS', icon: 'i-lucide-code-2' },
];

const layoutOptions = [
  {
    key: 'split',
    label: 'Conversão',
    description: 'Destaque e formulário lado a lado.',
    icon: 'i-lucide-panels-top-left',
  },
  {
    key: 'centered',
    label: 'Centralizado',
    description: 'Conteúdo e formulário no centro.',
    icon: 'i-lucide-align-center',
  },
  {
    key: 'minimal',
    label: 'Minimalista',
    description: 'Visual leve, direto e compacto.',
    icon: 'i-lucide-panel-top',
  },
];

const experienceOptions = [
  {
    value: 'form',
    label: 'Formulário direto',
    description: 'Coleta os dados de forma objetiva, sem etapa interativa.',
    icon: 'i-lucide-panels-top-left',
  },
  {
    value: 'roulette',
    label: 'Roleta',
    description: 'Cria uma experiência interativa antes do formulário.',
    icon: 'i-lucide-circle-dot-dashed',
  },
  {
    value: 'scratch',
    label: 'Raspadinha',
    description: 'Revela uma oferta antes de solicitar os dados do cliente.',
    icon: 'i-lucide-ticket-check',
  },
];

const palettes = [
  { name: 'Azul confiança', primary: '#2563EB', secondary: '#0F172A' },
  { name: 'Verde resultado', primary: '#16A34A', secondary: '#052E16' },
  { name: 'Roxo premium', primary: '#7C3AED', secondary: '#2E1065' },
  { name: 'Laranja energia', primary: '#EA580C', secondary: '#431407' },
  { name: 'Rosa conversão', primary: '#DB2777', secondary: '#500724' },

  { name: 'Grafite', primary: '#27272A', secondary: '#09090B' },
];
const inboxOptions = computed(() => [
  { value: '', label: 'Selecione uma caixa de site' },
  ...websiteInboxes.value.map(inbox => ({
    value: inbox.id,
    label: getInboxLabel(inbox),
  })),
]);

// Leads é o padrão; produtos de crédito só para empresas com módulo Corban. Uma captação
// antiga com produto Corban continua mostrando o valor salvo.
const { hasCorbanModule } = useCorbanModule();
const CORBAN_PRODUCT_OPTIONS = [
  { value: 'clt', label: 'CLT / Consignado' },
  { value: 'fgts', label: 'FGTS' },
  { value: 'inss', label: 'INSS' },
];
const productOptions = computed(() => [
  { value: 'leads', label: 'Leads' },
  ...CORBAN_PRODUCT_OPTIONS.filter(
    option => hasCorbanModule.value || option.value === editor.product
  ),
]);

const whatsappMessageInput = ref(null);
const whatsappNumberOptions = computed(() => [
  { value: 'manual', label: 'Inserir número manualmente' },
  ...whatsappInboxes.value.map(inbox => ({
    value: String(inbox.id),
    label: `${inbox.name} · ${inbox.phone_number}`,
  })),
]);
const selectedWhatsappNumber = computed({
  get: () =>
    String(
      whatsappInboxes.value.find(
        inbox => inbox.phone_number === editor.content.whatsapp_number
      )?.id || 'manual'
    ),
  set: id => {
    editor.content.whatsapp_number =
      whatsappInboxes.value.find(inbox => String(inbox.id) === id)
        ?.phone_number || '';
  },
});
const whatsappVariables = computed(() =>
  [
    { field: 'fullName', key: 'nome', label: 'Nome' },
    { field: 'phone', key: 'telefone', label: 'Telefone' },
    { field: 'cpf', key: 'cpf', label: 'CPF' },
    { field: 'email', key: 'email', label: 'E-mail' },
    { field: 'birthDate', key: 'nascimento', label: 'Nascimento' },
  ].filter(variable =>
    editor.form_fields.some(
      field => field.key === variable.field && field.enabled
    )
  )
);
const insertWhatsappVariable = async key => {
  const input = whatsappMessageInput.value;
  const text = editor.content.whatsapp_message || '';
  const start = input?.selectionStart ?? text.length;
  const end = input?.selectionEnd ?? start;
  const token = `{{${key}}}`;
  editor.content.whatsapp_message =
    text.slice(0, start) + token + text.slice(end);
  await nextTick();
  input?.focus();
  input?.setSelectionRange(start + token.length, start + token.length);
};

const postSubmitOptions = [
  { value: 'mixed', label: 'Misto — WhatsApp e chat no site' },
  { value: 'message', label: 'Exibir somente a confirmação' },
  { value: 'whatsapp', label: 'Continuar pelo WhatsApp (wa.me)' },
  { value: 'whatsapp_rotation', label: 'Roleta de caixas WhatsApp (wa.me)' },
  { value: 'webchat', label: 'Abrir o chat de uma caixa de site' },
];

const postSubmitIsValid = computed(() => {
  if (editor.content.post_submit_action === 'mixed') {
    const digits = editor.content.whatsapp_number.replace(/\D/g, '');
    return (
      digits.length >= 10 &&
      digits.length <= 15 &&
      Boolean(editor.content.webchat_inbox_id)
    );
  }
  if (editor.content.post_submit_action === 'whatsapp_rotation') {
    const ids = editor.content.whatsapp_inbox_ids;
    return (
      ids.length > 0 &&
      ids.every(id =>
        whatsappInboxes.value.some(inbox => String(inbox.id) === String(id))
      )
    );
  }
  if (editor.content.post_submit_action === 'whatsapp') {
    const digits = editor.content.whatsapp_number.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15;
  }
  if (editor.content.post_submit_action === 'webchat') {
    return Boolean(editor.content.webchat_inbox_id);
  }
  return true;
});

const contrastText = color => {
  const match = String(color || '').match(/^#([0-9a-f]{6})$/i);
  if (!match) return '#0f172a';
  const value = match[1];
  const [red, green, blue] = [0, 2, 4].map(index =>
    parseInt(value.slice(index, index + 2), 16)
  );
  return (red * 299 + green * 587 + blue * 114) / 1000 >= 150
    ? '#0f172a'
    : '#ffffff';
};

const previewPageText = computed(() =>
  contrastText(editor.design.background_color)
);
const previewCardText = computed(() => contrastText(editor.design.card_color));
const enabledFields = computed(() =>
  editor.form_fields.filter(field => field.enabled)
);

const canSave = computed(
  () =>
    editor.name.trim() &&
    editor.inbox_id &&
    enabledFields.value.length &&
    postSubmitIsValid.value
);

const isPublished = computed(() => editor.status === 'published');
const isDisabled = computed(() => editor.status === 'disabled');

const clone = value => JSON.parse(JSON.stringify(value));

const applyCapture = capture => {
  const defaults = defaultCapture();
  const saved = clone(capture);
  Object.assign(editor, defaults, saved, {
    design: { ...defaults.design, ...(saved.design || {}) },
    content: { ...defaults.content, ...(saved.content || {}) },
    seo: { ...defaults.seo, ...(saved.seo || {}) },
    tracking: { ...defaults.tracking, ...(saved.tracking || {}) },
    // Captures saved before the HTML/JS mode existed come without these fields.
    custom_html: saved.custom_html ?? defaults.custom_html,
    custom_js: saved.custom_js ?? defaults.custom_js,
  });
  editor.inbox_id = Number(editor.inbox_id) || '';
  editor.content.webchat_inbox_id =
    Number(editor.content.webchat_inbox_id) || '';
  selectedId.value = capture.id;
};

const loadCaptures = async preferredId => {
  loading.value = true;
  try {
    const { data } = await TalkviaLeadCapturesAPI.get();
    captures.value = data.captures || [];
    websiteInboxes.value = data.website_inboxes || [];
    whatsappInboxes.value = data.whatsapp_inboxes || [];
    defaultLegalContent.value = data.default_legal_content || {};
    const selected =
      captures.value.find(item => item.id === preferredId) || captures.value[0];
    if (selected) applyCapture(selected);
    else Object.assign(editor, defaultCapture());
  } catch {
    useAlert('Não foi possível carregar as captações.');
  } finally {
    loading.value = false;
  }
};

const selectCapture = capture => applyCapture(capture);

const newCapture = () => {
  selectedId.value = null;
  Object.assign(editor, defaultCapture());
  activeTab.value = 'config';
};

const payload = () => ({
  name: editor.name.trim(),
  inbox_id: editor.inbox_id,
  status: editor.status,
  product: editor.product,
  experience: editor.experience,
  template_key: editor.template_key,
  editor_mode: editor.editor_mode,
  form_fields: clone(editor.form_fields),
  design: clone(editor.design),
  content: clone(editor.content),
  seo: clone(editor.seo),
  tracking: clone(editor.tracking),
  custom_html: editor.custom_html,
  custom_js: editor.custom_js,
  public_path: editor.public_path?.trim().toLowerCase() || null,
});

const saveCapture = async () => {
  if (!canSave.value) {
    if (!postSubmitIsValid.value) {
      if (editor.content.post_submit_action === 'whatsapp_rotation') {
        useAlert('Selecione ao menos uma caixa WhatsApp válida para a roleta.');
        return null;
      }
      if (editor.content.post_submit_action === 'mixed') {
        useAlert(
          'Informe um WhatsApp válido com país e DDD e selecione uma caixa de site.'
        );
        return null;
      }
      useAlert(
        editor.content.post_submit_action === 'whatsapp'
          ? 'Informe um número de WhatsApp válido com país e DDD.'
          : 'Selecione a caixa de site que será aberta após o envio.'
      );
      return null;
    }

    useAlert(
      'Informe um nome, escolha uma caixa de site e habilite ao menos um campo.'
    );
    return null;
  }

  saving.value = true;
  try {
    const response = editor.id
      ? await TalkviaLeadCapturesAPI.update(editor.id, payload())
      : await TalkviaLeadCapturesAPI.create(payload());
    await loadCaptures(response.data.id);
    useAlert('Captação salva.');
    return response.data;
  } catch (error) {
    useAlert(
      error.response?.data?.message || 'Não foi possível salvar a captação.'
    );
    return null;
  } finally {
    saving.value = false;
  }
};

const updatingPublicPath = ref(false);
const savedPublicPath = computed(
  () =>
    captures.value.find(capture => capture.id === editor.id)?.public_path || ''
);
const normalizedPublicPath = computed(
  () => editor.public_path?.trim().toLowerCase() || ''
);
const publicPathChanged = computed(
  () => normalizedPublicPath.value !== savedPublicPath.value
);

// Saves only the short name, keeping any other unsaved edit in the editor.
const updatePublicLink = async () => {
  if (!editor.id) {
    useAlert('Salve a captação antes de definir o nome no link.');
    return;
  }
  updatingPublicPath.value = true;
  try {
    const { data } = await TalkviaLeadCapturesAPI.update(editor.id, {
      public_path: normalizedPublicPath.value || null,
    });
    editor.public_path = data.public_path || '';
    editor.public_url = data.public_url;
    const saved = captures.value.find(capture => capture.id === data.id);
    if (saved)
      Object.assign(saved, {
        public_path: data.public_path,
        public_url: data.public_url,
      });
    useAlert('Link atualizado.');
  } catch (error) {
    useAlert(
      error.response?.data?.message || 'Não foi possível atualizar o link.'
    );
  } finally {
    updatingPublicPath.value = false;
  }
};

const publishCapture = async () => {
  const saved = await saveCapture();
  if (!saved) return;

  try {
    const { data } = await TalkviaLeadCapturesAPI.publish(saved.id);
    await loadCaptures(data.id);
    useAlert('Captação publicada.');
  } catch {
    useAlert('Não foi possível publicar a captação.');
  }
};

const disableCapture = async () => {
  if (!editor.id || !isPublished.value) return;
  const saved = await saveCapture();
  if (!saved) return;
  saving.value = true;
  try {
    const { data } = await TalkviaLeadCapturesAPI.disable(saved.id);
    await loadCaptures(data.id);
    useAlert(
      'Captação inativada. O link agora exibe a página de indisponibilidade.'
    );
  } catch {
    useAlert('Não foi possível inativar a captação.');
  } finally {
    saving.value = false;
  }
};

const toggleAdvancedMode = enabled => {
  editor.editor_mode = enabled ? 'custom' : 'template';
  if (enabled) activeTab.value = 'code';
};

const insertFormPlaceholder = () => {
  if (editor.custom_html.includes('data-talkvia-form')) {
    useAlert('O ponto do formulário já está presente no HTML.');
    return;
  }
  editor.custom_html = `${editor.custom_html.trim()}\n<div data-talkvia-form></div>`;
};

const duplicateCapture = async () => {
  if (!editor.id) return;
  try {
    const { data } = await TalkviaLeadCapturesAPI.duplicate(editor.id);
    await loadCaptures(data.id);
    useAlert('Cópia criada como rascunho.');
  } catch {
    useAlert('Não foi possível duplicar a captação.');
  }
};

const applyPalette = palette => {
  editor.design.primary_color = palette.primary;
  editor.design.secondary_color = palette.secondary;
};

const copyPublicLink = async () => {
  if (!editor.public_url) return;
  try {
    await navigator.clipboard.writeText(editor.public_url);
    useAlert('Link copiado.');
  } catch {
    useAlert('Não foi possível copiar o link.');
  }
};

const openPublicLink = () => {
  if (editor.public_url)
    window.open(editor.public_url, '_blank', 'noopener,noreferrer');
};

onMounted(() => loadCaptures());
</script>

<template>
  <section class="h-full w-full overflow-y-auto bg-n-surface-1 px-5 py-5">
    <div class="mx-auto flex w-full max-w-[1500px] flex-col gap-5">
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 class="mb-1 text-2xl font-semibold text-n-slate-12">
            Captação de leads
          </h1>
          <p class="mb-0 text-sm text-n-slate-11">
            Crie páginas, receba o lead em uma caixa de site e acompanhe cada
            conversa no sistema.
          </p>
        </div>
        <Button
          label="Nova captação"
          icon="i-lucide-plus"
          @click="newCapture"
        />
      </header>

      <div
        class="grid min-h-[760px] gap-5 xl:grid-cols-[17rem_minmax(0,1fr)_24rem]"
      >
        <aside class="min-w-0 rounded-xl border border-n-weak bg-n-solid-2 p-3">
          <p class="px-2 pb-2 text-xs font-semibold uppercase text-n-slate-10">
            Suas captações
          </p>
          <div v-if="loading" class="p-3 text-sm text-n-slate-11">
            Carregando...
          </div>
          <div
            v-else-if="!captures.length"
            class="rounded-lg border border-dashed border-n-weak p-4 text-sm text-n-slate-11"
          >
            Nenhuma captação criada. Comece pelo botão acima.
          </div>
          <div v-else class="grid min-w-0 grid-cols-1 gap-2">
            <button
              v-for="capture in captures"
              :key="capture.id"
              type="button"
              class="min-w-0 w-full max-w-full overflow-hidden rounded-lg border p-3 text-left transition-colors"
              :title="capture.name"
              :class="
                capture.id === selectedId
                  ? 'border-n-brand bg-n-alpha-2'
                  : 'border-n-weak hover:bg-n-alpha-2'
              "
              @click="selectCapture(capture)"
            >
              <span
                class="block truncate text-sm font-semibold text-n-slate-12"
                >{{ capture.name }}</span
              >
              <span
                class="mt-1 flex items-center justify-between text-xs text-n-slate-10"
              >
                <span>{{ capture.submissions_count }} leads</span>
                <span
                  :class="
                    capture.status === 'published'
                      ? 'text-n-teal-11'
                      : capture.status === 'disabled'
                        ? 'text-n-ruby-11'
                        : 'text-n-amber-11'
                  "
                >
                  {{
                    capture.status === 'published'
                      ? 'Publicado'
                      : capture.status === 'disabled'
                        ? 'Inativa'
                        : 'Rascunho'
                  }}
                </span>
              </span>
            </button>
          </div>
        </aside>

        <main class="min-w-0">
          <div
            class="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
          >
            <Input
              v-model="editor.name"
              class="min-w-56 flex-1"
              custom-input-class="text-base font-semibold"
              placeholder="Nome da captação"
            />
            <div class="flex flex-wrap gap-2">
              <Button
                v-if="editor.id"
                label="Duplicar"
                icon="i-lucide-copy-plus"
                color="slate"
                variant="faded"
                @click="duplicateCapture"
              />
              <Button
                :label="saving ? 'Salvando...' : 'Salvar'"
                icon="i-lucide-save"
                color="slate"
                variant="faded"
                :disabled="saving"
                @click="saveCapture"
              />
              <Button
                v-if="isPublished"
                label="Inativar"
                icon="i-lucide-circle-pause"
                color="ruby"
                variant="faded"
                :disabled="saving"
                @click="disableCapture"
              />
              <Button
                :label="isPublished ? 'Atualizar publicação' : 'Publicar'"
                icon="i-lucide-globe-2"
                :disabled="saving"
                @click="publishCapture"
              />
            </div>
          </div>

          <div
            class="mb-4 grid grid-cols-2 gap-1 rounded-xl border border-n-weak bg-n-solid-2 p-1 md:grid-cols-5"
          >
            <button
              v-for="tab in tabs"
              :key="tab.key"
              type="button"
              class="flex h-10 items-center justify-center gap-2 rounded-lg text-sm font-medium"
              :class="
                activeTab === tab.key
                  ? 'bg-n-alpha-2 text-n-slate-12'
                  : 'text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="activeTab = tab.key"
            >
              <span :class="tab.icon" class="size-4" />{{ tab.label }}
            </button>
          </div>

          <div class="grid gap-4">
            <template v-if="activeTab === 'config'">
              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <h2 class="mb-4 text-sm font-semibold text-n-slate-12">
                  Destino e experiência
                </h2>
                <div class="grid gap-4 md:grid-cols-2">
                  <label class="grid gap-2 text-sm font-medium text-n-slate-12">
                    Caixa de site
                    <Select
                      v-model="editor.inbox_id"
                      :options="inboxOptions"
                      class="w-full [&>select]:w-full"
                    />
                  </label>
                  <label class="grid gap-2 text-sm font-medium text-n-slate-12">
                    Produto
                    <Select
                      v-model="editor.product"
                      :options="productOptions"
                      class="w-full [&>select]:w-full"
                    />
                  </label>
                  <label
                    class="flex items-center gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-4 md:col-span-2"
                  >
                    <span
                      class="grid size-10 shrink-0 place-items-center rounded-lg bg-n-violet-3 text-n-violet-11"
                    >
                      <span class="i-lucide-code-xml size-5" />
                    </span>
                    <span class="min-w-0 flex-1">
                      <strong class="block text-sm text-n-slate-12"
                        >Modo avançado HTML / JS</strong
                      >
                      <span
                        class="mt-1 block text-xs leading-5 text-n-slate-11"
                      >
                        Personalize o layout em um ambiente isolado. O
                        formulário validado do sistema continua responsável pelo
                        envio.
                      </span>
                    </span>
                    <Switch
                      :model-value="editor.editor_mode === 'custom'"
                      @update:model-value="toggleAdvancedMode"
                    />
                  </label>
                  <fieldset class="grid gap-2 md:col-span-2">
                    <legend class="text-sm text-n-slate-11">Experiência</legend>
                    <div class="grid gap-2 sm:grid-cols-3">
                      <button
                        v-for="experience in experienceOptions"
                        :key="experience.value"
                        type="button"
                        :aria-pressed="editor.experience === experience.value"
                        class="flex min-h-24 items-start gap-3 rounded-lg border p-3 text-left transition-colors"
                        :class="
                          editor.experience === experience.value
                            ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                            : 'border-n-weak text-n-slate-11 hover:border-n-strong hover:bg-n-alpha-2'
                        "
                        @click="editor.experience = experience.value"
                      >
                        <span
                          class="grid size-9 shrink-0 place-items-center rounded-lg"
                          :class="
                            editor.experience === experience.value
                              ? 'bg-n-brand/10 text-n-brand'
                              : 'bg-n-alpha-2 text-n-slate-11'
                          "
                        >
                          <span :class="experience.icon" class="size-4" />
                        </span>
                        <span class="min-w-0">
                          <strong class="block text-sm font-medium">
                            {{ experience.label }}
                          </strong>
                          <span
                            class="mt-1 block text-xs leading-5 text-n-slate-11"
                          >
                            {{ experience.description }}
                          </span>
                        </span>
                      </button>
                    </div>
                  </fieldset>
                  <section
                    v-if="editor.experience !== 'form'"
                    class="grid gap-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4 md:col-span-2 md:grid-cols-2"
                  >
                    <div class="md:col-span-2">
                      <h3 class="mb-1 text-sm font-semibold text-n-slate-12">
                        Personalização da experiência
                      </h3>
                      <p class="mb-0 text-xs text-n-slate-11">
                        Os valores abaixo já possuem um padrão e podem ser
                        personalizados para cada campanha.
                      </p>
                    </div>
                    <Input
                      v-model="editor.content.experience_title"
                      label="Título da experiência"
                    />
                    <Input
                      v-model="editor.content.experience_result"
                      label="Benefício revelado"
                    />
                    <label
                      class="grid gap-2 text-sm font-medium text-n-slate-12 md:col-span-2"
                    >
                      Descrição
                      <textarea
                        v-model="editor.content.experience_description"
                        rows="2"
                        class="rounded-lg border border-n-weak bg-n-surface-1 p-3 text-n-slate-12"
                      />
                    </label>
                    <Input
                      v-model="editor.content.experience_button_label"
                      label="Texto do botão"
                    />
                    <label
                      v-if="editor.experience === 'roulette'"
                      class="grid gap-2 text-sm font-medium text-n-slate-12"
                    >
                      Opções da roleta
                      <textarea
                        v-model="editor.content.roulette_options"
                        rows="4"
                        placeholder="Uma opção por linha"
                        class="rounded-lg border border-n-weak bg-n-surface-1 p-3 text-n-slate-12"
                      />
                    </label>
                  </section>
                </div>
              </section>

              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <div class="mb-4 flex items-start gap-3">
                  <span class="i-lucide-scale mt-0.5 size-5 text-n-brand" />
                  <div>
                    <h2 class="mb-1 text-sm font-semibold text-n-slate-12">
                      Termos e privacidade
                    </h2>
                    <p class="mb-0 text-xs leading-5 text-n-slate-11">
                      Os textos serão exibidos em links no rodapé da página
                      pública. Os dados da empresa vêm de Ajustes → Informações
                      da empresa.
                    </p>
                  </div>
                </div>
                <label
                  class="mb-4 flex items-center justify-between gap-4 text-sm text-n-slate-12"
                >
                  <span>
                    Usar termos padrão
                    <span class="mt-1 block text-xs text-n-slate-11">
                      Confira a prévia abaixo. Seus textos personalizados são
                      preservados ao alternar.
                    </span>
                  </span>
                  <Switch v-model="editor.content.use_default_terms" />
                </label>
                <div class="grid gap-4 lg:grid-cols-2">
                  <label class="grid gap-2 text-sm font-medium text-n-slate-12">
                    Termos de Uso
                    <textarea
                      v-model="termsOfUse"
                      :readonly="editor.content.use_default_terms"
                      rows="10"
                      maxlength="50000"
                      placeholder="Informe as condições de uso desta página e do atendimento."
                      class="min-h-56 rounded-lg border border-n-weak bg-n-surface-1 p-3 text-sm leading-6 text-n-slate-12"
                    />
                  </label>
                  <label class="grid gap-2 text-sm font-medium text-n-slate-12">
                    Política de Privacidade
                    <textarea
                      v-model="privacyPolicy"
                      :readonly="editor.content.use_default_terms"
                      rows="10"
                      maxlength="50000"
                      placeholder="Explique como os dados são coletados, usados, armazenados e como o titular pode exercer seus direitos."
                      class="min-h-56 rounded-lg border border-n-weak bg-n-surface-1 p-3 text-sm leading-6 text-n-slate-12"
                    />
                  </label>
                </div>
              </section>

              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <h2 class="mb-1 text-sm font-semibold text-n-slate-12">
                  Formulário padrão do sistema
                </h2>
                <p class="mb-4 text-xs text-n-slate-11">
                  Os campos habilitados são validados e enviados para a conversa
                  da caixa de site.
                </p>
                <div class="grid gap-2 md:grid-cols-2">
                  <div
                    v-for="field in editor.form_fields"
                    :key="field.key"
                    class="grid gap-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4 transition-colors hover:border-n-strong"
                  >
                    <div class="flex items-center gap-3">
                      <span
                        class="grid size-9 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
                      >
                        <span class="i-lucide-text-cursor-input size-4" />
                      </span>
                      <div class="min-w-0 flex-1">
                        <p class="mb-0 text-sm font-semibold text-n-slate-12">
                          {{ fieldLabels[field.key] }}
                        </p>
                        <p class="mb-0 mt-0.5 text-xs text-n-slate-10">
                          Configure a exibição e a validação deste campo.
                        </p>
                      </div>
                    </div>
                    <div
                      class="flex items-center justify-between border-t border-n-weak pt-3"
                    >
                      <span class="text-xs font-medium text-n-slate-11"
                        >Exibir campo</span
                      >
                      <Switch v-model="field.enabled" />
                    </div>
                    <div class="flex items-center justify-between">
                      <span
                        class="text-xs font-medium"
                        :class="
                          field.enabled ? 'text-n-slate-11' : 'text-n-slate-9'
                        "
                      >
                        Obrigatório
                      </span>
                      <Switch
                        v-model="field.required"
                        :disabled="!field.enabled"
                      />
                    </div>
                  </div>
                </div>
              </section>

              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <div class="mb-4 flex items-start gap-3">
                  <span
                    class="i-lucide-calendar-off mt-0.5 size-5 text-n-ruby-10"
                  />
                  <div>
                    <h2 class="mb-1 text-sm font-semibold text-n-slate-12">
                      Página inativa
                    </h2>
                    <p class="mb-0 text-xs leading-5 text-n-slate-11">
                      Este conteúdo substitui o formulário quando a captação for
                      pausada.
                    </p>
                  </div>
                </div>
                <div class="grid gap-4">
                  <Input
                    v-model="editor.content.inactive_title"
                    label="Título"
                  />
                  <label class="grid gap-2 text-sm font-medium text-n-slate-12">
                    Mensagem
                    <textarea
                      v-model="editor.content.inactive_message"
                      rows="3"
                      class="rounded-lg border border-n-weak bg-n-surface-1 p-3 text-n-slate-12"
                    />
                  </label>
                </div>
              </section>
            </template>

            <template v-else-if="activeTab === 'design'">
              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <h2 class="mb-4 text-sm font-semibold text-n-slate-12">
                  Modelo do layout
                </h2>
                <div class="grid gap-2 md:grid-cols-3">
                  <button
                    v-for="item in layoutOptions"
                    :key="item.key"
                    type="button"
                    class="flex min-h-24 items-center gap-3 rounded-lg border p-4 text-left transition-colors"
                    :class="
                      editor.template_key === item.key
                        ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                        : 'border-n-weak text-n-slate-11'
                    "
                    @click="editor.template_key = item.key"
                  >
                    <span
                      class="grid size-10 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
                    >
                      <span :class="item.icon" class="size-5" />
                    </span>
                    <span class="min-w-0">
                      <span class="block text-sm font-semibold">
                        {{ item.label }}
                      </span>
                      <span
                        class="mt-1 block text-xs font-normal text-n-slate-10"
                      >
                        {{ item.description }}
                      </span>
                    </span>
                  </button>
                </div>
              </section>
              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <h2 class="mb-4 text-sm font-semibold text-n-slate-12">
                  Paletas prontas
                </h2>
                <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  <button
                    v-for="palette in palettes"
                    :key="palette.name"
                    type="button"
                    class="flex items-center gap-3 rounded-lg border border-n-weak p-3 text-sm text-n-slate-12"
                    @click="applyPalette(palette)"
                  >
                    <span class="flex"
                      ><i
                        class="size-5 rounded-full"
                        :style="{ background: palette.primary }" /><i
                        class="-ml-1 size-5 rounded-full"
                        :style="{ background: palette.secondary }" /></span
                    >{{ palette.name }}
                  </button>
                </div>
                <div class="mt-5 grid gap-3 sm:grid-cols-2">
                  <label
                    v-for="color in [
                      { key: 'primary_color', label: 'Cor principal' },
                      { key: 'secondary_color', label: 'Cor de apoio' },
                      { key: 'background_color', label: 'Fundo' },
                      { key: 'card_color', label: 'Cartão' },
                      { key: 'text_color', label: 'Texto' },
                    ]"
                    :key="color.key"
                    class="flex items-center justify-between rounded-lg border border-n-weak p-3 text-sm text-n-slate-12"
                  >
                    {{ color.label
                    }}<span class="flex items-center gap-2"
                      ><code>{{ editor.design[color.key] }}</code
                      ><input
                        v-model="editor.design[color.key]"
                        type="color"
                        class="size-8 cursor-pointer rounded border-0 bg-transparent"
                    /></span>
                  </label>
                </div>
              </section>
            </template>

            <template v-else-if="activeTab === 'content'">
              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <h2 class="mb-4 text-sm font-semibold text-n-slate-12">
                  Textos da página
                </h2>
                <div class="grid gap-4">
                  <label
                    v-for="field in [
                      { key: 'eyebrow', label: 'Chamada superior' },
                      { key: 'headline', label: 'Título principal' },
                      { key: 'description', label: 'Descrição' },
                      { key: 'form_title', label: 'Título do formulário' },
                      { key: 'button_label', label: 'Texto do botão' },
                      { key: 'success_message', label: 'Mensagem de sucesso' },
                    ]"
                    :key="field.key"
                    class="grid gap-2 text-sm text-n-slate-11"
                    >{{ field.label }}
                    <textarea
                      v-model="editor.content[field.key]"
                      rows="2"
                      class="rounded-lg border border-n-weak bg-n-surface-1 p-3 text-n-slate-12"
                    />
                  </label>
                </div>
              </section>

              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <div class="mb-5 flex items-start gap-3">
                  <span
                    class="i-lucide-mouse-pointer-click mt-0.5 size-5 text-n-brand"
                  />
                  <div>
                    <h2 class="mb-1 text-sm font-semibold text-n-slate-12">
                      Depois do envio
                    </h2>
                    <p class="mb-0 text-xs leading-5 text-n-slate-11">
                      Defina o que acontece após o visitante clicar no botão e
                      os dados serem recebidos.
                    </p>
                  </div>
                </div>
                <div class="grid gap-4 md:grid-cols-2">
                  <label class="grid gap-2 text-sm font-medium text-n-slate-12">
                    Ação de continuidade
                    <Select
                      v-model="editor.content.post_submit_action"
                      :options="postSubmitOptions"
                      class="w-full [&>select]:w-full"
                    />
                  </label>
                  <Input
                    v-if="
                      !['message', 'mixed'].includes(
                        editor.content.post_submit_action
                      )
                    "
                    v-model="editor.content.post_submit_button_label"
                    label="Texto do botão de continuidade"
                    placeholder="Continuar atendimento"
                  />
                </div>
                <div
                  v-if="
                    editor.content.post_submit_action === 'whatsapp_rotation'
                  "
                  class="mt-4 grid min-w-0 gap-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
                >
                  <label
                    class="grid min-w-0 gap-2 text-sm font-medium text-n-slate-12"
                  >
                    Caixas WhatsApp da roleta
                    <TagMultiSelectComboBox
                      v-model="editor.content.whatsapp_inbox_ids"
                      :options="whatsappInboxOptions"
                      placeholder="Selecione as caixas WhatsApp"
                      search-placeholder="Buscar caixa WhatsApp"
                      empty-state="Nenhuma caixa WhatsApp disponível"
                      :has-error="!postSubmitIsValid"
                    />
                  </label>
                  <p class="mb-0 text-xs leading-5 text-n-slate-11">
                    Cada envio concluído recebe a próxima caixa selecionada, em
                    sequência. O botão abre o WhatsApp pelo número cadastrado na
                    caixa. Somente caixas WhatsApp com número válido participam.
                  </p>
                  <label class="grid gap-2 text-sm font-medium text-n-slate-12">
                    Mensagem inicial
                    <textarea
                      ref="whatsappMessageInput"
                      v-model="editor.content.whatsapp_message"
                      rows="3"
                      class="min-h-20 rounded-lg border-0 bg-n-alpha-black2 p-3 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-slate-5 focus:outline-n-brand dark:outline-n-weak"
                    />
                  </label>
                </div>
                <div
                  v-if="
                    ['whatsapp', 'mixed'].includes(
                      editor.content.post_submit_action
                    )
                  "
                  class="mt-4 grid gap-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4 md:grid-cols-2"
                >
                  <Input
                    v-if="editor.content.post_submit_action === 'mixed'"
                    v-model="editor.content.whatsapp_button_label"
                    label="Texto do botão do WhatsApp"
                    placeholder="Continuar pelo WhatsApp"
                  />
                  <label class="grid gap-2 text-sm font-medium text-n-slate-12">
                    Destino do WhatsApp
                    <Select
                      v-model="selectedWhatsappNumber"
                      :options="whatsappNumberOptions"
                      class="w-full [&>select]:w-full"
                    />
                  </label>
                  <Input
                    v-if="selectedWhatsappNumber === 'manual'"
                    v-model="editor.content.whatsapp_number"
                    label="Número do WhatsApp"
                    placeholder="5511999999999"
                    message="Informe país, DDD e número. O link wa.me será criado automaticamente."
                    :message-type="postSubmitIsValid ? 'info' : 'error'"
                  />
                  <label class="grid gap-2 text-sm font-medium text-n-slate-12">
                    Mensagem inicial
                    <textarea
                      ref="whatsappMessageInput"
                      v-model="editor.content.whatsapp_message"
                      rows="3"
                      class="min-h-20 rounded-lg border-0 bg-n-alpha-black2 p-3 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-slate-5 placeholder:text-n-slate-10 focus:outline-n-brand dark:outline-n-weak"
                    />
                  </label>
                </div>
                <div
                  v-if="
                    ['webchat', 'mixed'].includes(
                      editor.content.post_submit_action
                    )
                  "
                  class="mt-4 grid gap-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
                >
                  <Input
                    v-if="editor.content.post_submit_action === 'mixed'"
                    v-model="editor.content.webchat_button_label"
                    label="Texto do botão do chat"
                    placeholder="Conversar no site"
                  />
                  <label class="grid gap-2 text-sm font-medium text-n-slate-12">
                    Caixa de site para o chat
                    <Select
                      v-model="editor.content.webchat_inbox_id"
                      :options="inboxOptions"
                      class="w-full [&>select]:w-full"
                    />
                  </label>
                  <div
                    class="flex gap-2 rounded-lg bg-n-blue-3 p-3 text-xs leading-5 text-n-blue-11"
                  >
                    <span class="i-lucide-info mt-0.5 size-4 shrink-0" />
                    <p class="mb-0">
                      {{
                        editor.content.post_submit_action === 'mixed'
                          ? 'Após o envio, o visitante escolhe entre os dois botões. O chat abre somente ao clicar em conversar no site.'
                          : 'Após o envio, a página carregará o widget oficial dessa caixa e abrirá o atendimento para o visitante.'
                      }}
                    </p>
                  </div>
                </div>
                <div
                  v-if="
                    ['whatsapp', 'whatsapp_rotation', 'mixed'].includes(
                      editor.content.post_submit_action
                    )
                  "
                  class="mt-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
                >
                  <p class="mb-2 text-xs text-n-slate-11">
                    Inserir dados do formulário na mensagem do WhatsApp:
                  </p>
                  <div class="flex flex-wrap gap-2">
                    <Button
                      v-for="variable in whatsappVariables"
                      :key="variable.key"
                      size="sm"
                      variant="faded"
                      :label="variable.label"
                      @mousedown.prevent
                      @click="insertWhatsappVariable(variable.key)"
                    />
                  </div>
                  <p class="mb-0 mt-2 text-xs text-n-slate-11">
                    As variáveis são preenchidas após o envio. O visitante
                    confirma o envio da mensagem no WhatsApp.
                  </p>
                </div>
              </section>
            </template>

            <template v-else-if="activeTab === 'seo'">
              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <h2 class="mb-4 text-sm font-semibold text-n-slate-12">SEO</h2>
                <div class="grid gap-4 md:grid-cols-2">
                  <label
                    v-for="field in [
                      { key: 'title', label: 'Título SEO' },
                      { key: 'description', label: 'Meta description' },
                      { key: 'keywords', label: 'Palavras-chave' },
                      { key: 'canonical_url', label: 'URL canônica' },
                    ]"
                    :key="field.key"
                    class="grid gap-2 text-sm text-n-slate-11"
                    >{{ field.label
                    }}<input
                      v-model="editor.seo[field.key]"
                      class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-n-slate-12"
                  /></label>
                  <label class="flex items-center gap-3 text-sm text-n-slate-12"
                    ><input v-model="editor.seo.noindex" type="checkbox" />
                    Impedir indexação desta captação</label
                  >
                </div>
              </section>
              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <h2 class="mb-1 text-sm font-semibold text-n-slate-12">
                  Anúncios e pixels
                </h2>
                <p class="mb-4 text-xs text-n-slate-11">
                  Informe somente os IDs. O sistema carrega os scripts oficiais
                  com segurança.
                </p>
                <div class="grid gap-4 md:grid-cols-2">
                  <label
                    v-for="field in [
                      { key: 'ga4_id', label: 'Google Analytics 4 (G-...)' },
                      { key: 'gtm_id', label: 'Google Tag Manager (GTM-...)' },
                      { key: 'google_ads_id', label: 'Google Ads (AW-...)' },
                      { key: 'google_ads_label', label: 'Label de conversão' },
                      { key: 'meta_pixel_id', label: 'Meta Pixel ID' },
                    ]"
                    :key="field.key"
                    class="grid gap-2 text-sm text-n-slate-11"
                    >{{ field.label
                    }}<input
                      v-model="editor.tracking[field.key]"
                      class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-n-slate-12"
                  /></label>
                </div>
              </section>
            </template>

            <template v-else>
              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <div
                  class="mb-5 flex flex-col gap-3 rounded-xl border border-n-violet-7 bg-n-violet-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div class="flex gap-3">
                    <span
                      class="i-lucide-shield-check mt-0.5 size-5 shrink-0 text-n-violet-11"
                    />
                    <div>
                      <h2 class="mb-1 text-sm font-semibold text-n-violet-12">
                        Editor avançado protegido
                      </h2>
                      <p class="mb-0 text-xs leading-5 text-n-violet-11">
                        O código executa em iframe isolado. Use o ponto do
                        formulário para manter máscaras, validações e criação da
                        conversa.
                      </p>
                    </div>
                  </div>
                  <Button
                    label="Inserir formulário"
                    icon="i-lucide-panel-bottom"
                    color="slate"
                    variant="faded"
                    @click="insertFormPlaceholder"
                  />
                </div>
                <div class="grid gap-4 xl:grid-cols-2">
                  <label class="grid gap-2 text-sm text-n-slate-11">
                    <span class="flex items-center justify-between">
                      <strong class="text-n-slate-12"
                        >HTML personalizado</strong
                      >
                      <span class="text-xs tabular-nums text-n-slate-9"
                        >{{ editor.custom_html.length }} / 100.000</span
                      >
                    </span>
                    <textarea
                      v-model="editor.custom_html"
                      rows="14"
                      spellcheck="false"
                      class="rounded-lg border border-n-weak bg-n-surface-1 p-3 font-mono text-xs text-n-slate-12"
                    />
                  </label>
                  <label class="grid gap-2 text-sm text-n-slate-11">
                    <span class="flex items-center justify-between">
                      <strong class="text-n-slate-12"
                        >JavaScript personalizado</strong
                      >
                      <span class="text-xs tabular-nums text-n-slate-9"
                        >{{ editor.custom_js.length }} / 50.000</span
                      >
                    </span>
                    <textarea
                      v-model="editor.custom_js"
                      rows="10"
                      spellcheck="false"
                      class="rounded-lg border border-n-weak bg-n-surface-1 p-3 font-mono text-xs text-n-slate-12"
                    />
                  </label>
                </div>
                <div
                  class="mt-4 grid gap-2 rounded-xl border border-n-weak bg-n-alpha-1 p-4 text-xs leading-5 text-n-slate-11 sm:grid-cols-3"
                >
                  <span>✓ Máscaras de telefone e CPF preservadas</span>
                  <span>✓ Envio por ponte segura do sistema</span>
                  <span
                    >✓ Evento disponível: <code>talkvia:submitted</code></span
                  >
                </div>
              </section>
            </template>
          </div>
        </main>

        <aside class="min-w-0">
          <div class="sticky top-4 grid gap-4">
            <section class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
              <div class="flex items-center justify-between gap-3">
                <div>
                  <p class="mb-1 text-sm font-semibold text-n-slate-12">
                    Publicação
                  </p>
                  <p class="mb-0 text-xs text-n-slate-11">
                    {{ editor.submissions_count || 0 }} leads recebidos
                  </p>
                </div>
                <span
                  class="rounded-full px-2 py-1 text-xs"
                  :class="
                    isPublished
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : isDisabled
                        ? 'bg-n-ruby-3 text-n-ruby-11'
                        : 'bg-n-amber-3 text-n-amber-11'
                  "
                  >{{
                    isPublished
                      ? 'Publicado'
                      : isDisabled
                        ? 'Inativa'
                        : 'Rascunho'
                  }}</span
                >
              </div>
              <div v-if="editor.public_url" class="mt-3 flex gap-2">
                <input
                  :value="editor.public_url"
                  readonly
                  class="h-9 min-w-0 flex-1 rounded-lg border border-n-weak bg-n-surface-1 px-2 text-xs text-n-slate-11"
                /><Button
                  icon="i-lucide-copy"
                  size="sm"
                  color="slate"
                  variant="faded"
                  @click="copyPublicLink"
                /><Button
                  icon="i-lucide-external-link"
                  size="sm"
                  color="slate"
                  variant="faded"
                  @click="openPublicLink"
                />
              </div>
              <div v-if="hasActiveCustomDomain" class="mt-3 grid gap-1">
                <label
                  for="capture-public-path"
                  class="text-xs font-medium text-n-slate-12"
                  >Nome no link</label
                >
                <form
                  class="flex min-w-0 items-center gap-2"
                  @submit.prevent="updatePublicLink"
                >
                  <div
                    class="flex min-w-0 flex-1 items-center overflow-hidden rounded-lg border border-n-weak bg-n-surface-1"
                  >
                    <span
                      class="shrink-0 pl-2 text-xs text-n-slate-10"
                      :title="`${customDomain.hostname}/`"
                      >/</span
                    >
                    <input
                      id="capture-public-path"
                      v-model="editor.public_path"
                      class="h-9 min-w-0 flex-1 border-0 bg-transparent px-1 text-xs text-n-slate-12 outline-none"
                      placeholder="diagnostico"
                      maxlength="60"
                    />
                  </div>
                  <Button
                    type="submit"
                    size="sm"
                    label="Atualizar link"
                    :is-loading="updatingPublicPath"
                    :disabled="!publicPathChanged || updatingPublicPath"
                  />
                </form>
                <p class="mb-0 text-[11px] text-n-slate-11">
                  Letras minúsculas, números e hífen. Clique em Atualizar link
                  para aplicar; o link com código continua funcionando.
                </p>
              </div>
              <CustomDomainCard
                v-if="isAdmin"
                @changed="loadCaptures"
                @loaded="customDomain = $event"
              />
            </section>

            <section
              class="overflow-hidden rounded-xl border border-n-weak bg-white shadow-xl"
            >
              <div
                class="relative flex h-9 items-center gap-1.5 bg-slate-100 px-3"
              >
                <i class="size-2.5 rounded-full bg-red-400" /><i
                  class="size-2.5 rounded-full bg-amber-400"
                /><i class="size-2.5 rounded-full bg-teal-500" />
                <span
                  class="pointer-events-none absolute inset-x-16 truncate text-center text-[10px] font-semibold text-slate-500"
                >
                  {{
                    editor.template_key === 'split'
                      ? 'Conversão'
                      : editor.template_key === 'centered'
                        ? 'Centralizado'
                        : 'Minimalista'
                  }}
                  · prévia aproximada
                </span>
              </div>
              <div
                v-if="editor.editor_mode === 'custom'"
                class="grid min-h-[560px] place-items-center p-6 text-center text-slate-600"
              >
                <div>
                  <span class="i-lucide-code-2 mx-auto mb-3 block size-8" />
                  <p class="font-semibold">HTML / JS personalizado</p>
                  <p class="text-xs">
                    A execução fica disponível somente no link público por
                    segurança.
                  </p>
                </div>
              </div>
              <div
                v-else
                class="min-h-[500px] p-3"
                :style="{
                  background:
                    editor.template_key === 'centered'
                      ? `linear-gradient(145deg, color-mix(in srgb, ${editor.design.secondary_color} 82%, #020617), color-mix(in srgb, ${editor.design.primary_color} 72%, #052e16))`
                      : editor.design.background_color,
                }"
              >
                <div
                  class="p-5 text-center"
                  :class="{
                    'rounded-2xl pb-14 text-white shadow-lg':
                      editor.template_key === 'split',
                    'pb-3 text-white': editor.template_key === 'centered',
                    'pb-3 pt-6': editor.template_key === 'minimal',
                  }"
                  :style="{
                    background:
                      editor.template_key === 'split'
                        ? `linear-gradient(145deg, color-mix(in srgb, ${editor.design.secondary_color} 78%, #020617), color-mix(in srgb, ${editor.design.primary_color} 72%, #052e16))`
                        : 'transparent',
                    color:
                      editor.template_key === 'minimal'
                        ? previewPageText
                        : '#ffffff',
                  }"
                >
                  <span
                    class="mb-3 inline-flex rounded-full border border-current/20 px-2.5 py-1 text-[8px] font-semibold opacity-80"
                  >
                    Atendimento oficial
                  </span>
                  <p
                    class="mb-2 text-[10px] font-bold uppercase tracking-widest opacity-75"
                  >
                    {{ editor.content.eyebrow }}
                  </p>
                  <h2
                    class="mx-auto mb-2 max-w-[300px] text-2xl font-bold leading-tight"
                    :style="{
                      color:
                        editor.template_key === 'minimal'
                          ? previewPageText
                          : '#ffffff',
                    }"
                  >
                    {{ editor.content.headline }}
                  </h2>
                  <p class="mx-auto mb-0 max-w-[290px] text-xs opacity-80">
                    {{ editor.content.description }}
                  </p>
                  <div
                    v-if="editor.template_key !== 'minimal'"
                    class="mt-4 flex flex-wrap justify-center gap-1.5"
                  >
                    <span
                      class="rounded-full border border-current/15 px-2 py-1 text-[8px] font-semibold opacity-80"
                    >
                      ✓ Dados protegidos
                    </span>
                    <span
                      class="rounded-full border border-current/15 px-2 py-1 text-[8px] font-semibold opacity-80"
                    >
                      ✓ Preenchimento rápido
                    </span>
                  </div>
                </div>
                <div
                  class="relative"
                  :class="{
                    '-mt-10 px-3 pb-4': editor.template_key === 'split',
                    'mt-3 px-1 pb-4': editor.template_key === 'centered',
                    'mt-3 px-2 pb-4': editor.template_key === 'minimal',
                  }"
                >
                  <div
                    class="rounded-2xl border p-5"
                    :class="
                      editor.template_key === 'minimal'
                        ? 'border-slate-200 shadow-sm'
                        : 'border-white/70 shadow-xl'
                    "
                    :style="{
                      background: editor.design.card_color,
                      color: previewCardText,
                    }"
                  >
                    <template v-if="editor.experience !== 'form'">
                      <p
                        class="mb-1 text-center text-[8px] font-bold uppercase tracking-widest text-emerald-600"
                      >
                        Experiência interativa
                      </p>
                      <p class="mb-1 text-center text-base font-bold">
                        {{ editor.content.experience_title }}
                      </p>
                      <p
                        class="mb-3 text-center text-[10px] leading-4 opacity-60"
                      >
                        {{ editor.content.experience_description }}
                      </p>
                      <div
                        v-if="editor.experience === 'roulette'"
                        class="relative mx-auto mb-4 grid size-32 place-items-center rounded-full border-[7px] border-white/70 shadow-lg"
                        :style="{
                          background: `repeating-conic-gradient(color-mix(in srgb, ${editor.design.primary_color} 78%, #0f172a) 0deg 45deg, color-mix(in srgb, ${editor.design.secondary_color} 76%, #ffffff) 45deg 90deg)`,
                        }"
                      >
                        <span
                          class="grid size-11 place-items-center rounded-full border-4 border-white/70 bg-white text-[9px] font-black text-slate-800"
                        >
                          GIRE
                        </span>
                      </div>
                      <div
                        v-else
                        class="mb-4 grid h-24 place-items-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-100 text-[10px] font-bold text-slate-500"
                      >
                        RASPE AQUI
                      </div>
                      <button
                        type="button"
                        class="h-10 w-full rounded-lg text-xs font-bold text-white"
                        :style="{
                          background: `color-mix(in srgb, ${editor.design.primary_color} 72%, #0f172a)`,
                        }"
                      >
                        {{ editor.content.experience_button_label }}
                      </button>
                    </template>
                    <template v-else>
                      <p class="mb-4 text-center text-sm font-bold">
                        {{ editor.content.form_title }}
                      </p>
                      <div class="grid gap-2">
                        <div
                          v-for="field in enabledFields.slice(0, 4)"
                          :key="field.key"
                          class="flex h-10 items-center rounded-lg border border-slate-200 bg-slate-50 px-3 text-xs text-slate-500"
                        >
                          {{ fieldLabels[field.key] }}
                        </div>
                        <button
                          type="button"
                          class="mt-2 h-10 rounded-lg text-xs font-bold text-white"
                          :style="{
                            background: `color-mix(in srgb, ${editor.design.primary_color} 72%, #0f172a)`,
                          }"
                        >
                          {{ editor.content.button_label }}
                        </button>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </aside>
      </div>
    </div>
  </section>
</template>
